import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, GraduationCap, Award, Edit2, X, Loader2, Plus } from 'lucide-react';
import { candidateServiceApi } from '../../../candidate/services/candidateServiceApi';
import * as onboardingApi from '../services/onboardingApi';
import { useOnboardingRedirect } from '../hooks/useOnboardingRedirect';
import { useFlowProtection, setFlowData } from '../../hooks/useFlowProtection';

interface BaseItem {
    id: string;
    type: 'experience' | 'education' | 'certification';
}

interface ExperienceItem extends BaseItem {
    type: 'experience';
    title: string;
    company: string;
    period: string;
    description: string;
}

interface EducationItem extends BaseItem {
    type: 'education';
    degree: string;
    school: string;
    details: string;
    year: string;
}

interface CertificationItem extends BaseItem {
    type: 'certification';
    name: string;
    issuer: string;
    year: string;
}

type ResumeItem = ExperienceItem | EducationItem | CertificationItem;

const BackgroundGrid = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
        {/* Ellipse 1: Main Gradient Background */}
        <div
            className="absolute"
            style={{
                width: '2618.47px',
                height: '1939.34px',
                left: '-448.14px',
                top: '-919.31px',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                transform: 'rotate(26.84deg)',
                transformOrigin: 'top left',
            }}
        />

        {/* Ellipse 2827: Blurred Overlay */}
        <div
            className="absolute hidden md:block"
            style={{
                width: '640px',
                height: '458px',
                left: '50%',
                top: '282px',
                transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
                background: 'rgba(255, 255, 255, 0.4)',
                filter: 'blur(93.2px)',
                borderRadius: '53px',
            }}
        />

        {/* Grid Lines */}
        <div className="absolute inset-0 flex justify-center pointer-events-none">
            <div className="w-full h-full max-w-[1920px] relative">
                {/* Vertical Lines */}
                {[...Array(12)].map((_, i) => (
                    <div
                        key={`v-${i}`}
                        className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ left: `${105 + (i * 120)}px` }}
                    />
                ))}

                {/* Horizontal Lines */}
                {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
                    <div
                        key={`h-${i}`}
                        className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ top: `${top}px` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const Stepper = ({ currentStep }: { currentStep: string }) => {
    const steps = [
        { label: 'Upload your resume', id: 'upload' },
        { label: 'Resume Review & Edit', id: 'review' },
        { label: 'Career Goals & Aspirations', id: 'goals' }
    ];

    const getStatus = (stepId: string) => {
        if (currentStep === 'review') {
            if (stepId === 'upload') return 'completed';
            if (stepId === 'review') return 'active';
            return 'upcoming';
        }
        return 'upcoming';
    };

    return (
        <div className="flex items-center justify-center w-full max-w-3xl mx-auto mb-12 relative z-10 px-4">
            {steps.map((step, idx) => {
                const status = getStatus(step.id);
                const isActive = status === 'active';
                const isCompleted = status === 'completed';

                return (
                    <div key={idx} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center relative">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-[3px] z-10 transition-colors duration-300 ${
                                isActive || isCompleted
                                    ? 'bg-[#8F9FB8] border-[#CBD5E1]'
                                    : 'bg-white border-[#CBD5E1]'
                            }`}>
                                {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                                {isCompleted && <div className="w-2 h-2 bg-white rounded-full opacity-0" />}
                            </div>
                            <span className={`absolute top-8 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                                isActive ? 'text-[#015FA6] font-bold' : 'text-[#015FA6]/70'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                        {idx !== steps.length - 1 && (
                            <div className={`flex-1 h-[2px] mx-2 transition-colors duration-300 ${
                                isCompleted ? 'bg-[#8F9FB8]' : 'bg-[#CBD5E1]'
                            }`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const EditModal = ({
    isOpen,
    onClose,
    item,
    onSave,
    itemType
}: {
    isOpen: boolean;
    onClose: () => void;
    item: ResumeItem | null;
    onSave: (updatedItem: ResumeItem) => void;
    itemType?: 'experience' | 'education' | 'certification';
}) => {
    const [formData, setFormData] = useState<ResumeItem | null>(item);

    React.useEffect(() => {
        if (item) {
            setFormData(item);
        } else if (itemType) {
            // Create new empty item based on type
            const newId = `new-${Date.now()}`;
            if (itemType === 'experience') {
                setFormData({
                    id: newId,
                    type: 'experience',
                    title: '',
                    company: '',
                    period: '',
                    description: ''
                });
            } else if (itemType === 'education') {
                setFormData({
                    id: newId,
                    type: 'education',
                    degree: '',
                    school: '',
                    details: '',
                    year: ''
                });
            } else if (itemType === 'certification') {
                setFormData({
                    id: newId,
                    type: 'certification',
                    name: '',
                    issuer: '',
                    year: ''
                });
            }
        }
    }, [item, itemType]);

    if (!isOpen || !formData) return null;

    const handleChange = (field: string, value: string) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData) onSave(formData);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative z-10 overflow-hidden animate-fade-in-up">
                <div className="bg-[#015FA6] p-4 flex justify-between items-center">
                    <h3 className="text-white font-bold text-lg">
                        {item ? 'Edit' : 'Add'} {formData.type === 'experience' ? 'Experience' : formData.type === 'education' ? 'Education' : 'Certification'}
                    </h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {formData.type === 'experience' && (
                        <>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Job Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => handleChange('title', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Company</label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={e => handleChange('company', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Time Period</label>
                                <input
                                    type="text"
                                    value={formData.period}
                                    onChange={e => handleChange('period', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => handleChange('description', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] h-24 resize-none"
                                />
                            </div>
                        </>
                    )}

                    {formData.type === 'education' && (
                        <>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Degree</label>
                                <input
                                    type="text"
                                    value={formData.degree}
                                    onChange={e => handleChange('degree', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">School</label>
                                <input
                                    type="text"
                                    value={formData.school}
                                    onChange={e => handleChange('school', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Details</label>
                                <input
                                    type="text"
                                    value={formData.details}
                                    onChange={e => handleChange('details', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Graduation Year</label>
                                <input
                                    type="text"
                                    value={formData.year}
                                    onChange={e => handleChange('year', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                        </>
                    )}

                    {formData.type === 'certification' && (
                        <>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Certification Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={e => handleChange('name', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Issuer</label>
                                <input
                                    type="text"
                                    value={formData.issuer}
                                    onChange={e => handleChange('issuer', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Year</label>
                                <input
                                    type="text"
                                    value={formData.year}
                                    onChange={e => handleChange('year', e.target.value)}
                                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                />
                            </div>
                        </>
                    )}

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 py-3 px-4 rounded-lg bg-[#015FA6] text-white font-bold hover:bg-[#014f8c] shadow-lg shadow-blue-900/20 transition-all transform active:scale-95"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Card = ({
    title,
    icon: Icon,
    children,
    onAdd
}: {
    title: string;
    icon: any;
    children: React.ReactNode;
    onAdd: () => void;
}) => (
    <div className="bg-white rounded-2xl shadow-[0px_2px_15px_rgba(0,0,0,0.05)] border border-gray-100 flex flex-col h-full p-4">
        <div className="pb-2 mb-3">
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center text-[#015FA6]">
                    <Icon size={16} />
                </div>
                <h2 className="text-[#015FA6] font-manrope font-bold text-lg">{title}</h2>
            </div>
        </div>
        <div className="flex-1 space-y-2.5 overflow-y-auto custom-scrollbar">
            {children}
            <button
                onClick={onAdd}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border border-dashed border-[#015FA6]/40 text-[#015FA6] text-sm hover:bg-[#015FA6]/5 transition-colors"
            >
                <Plus size={16} />
                Add {title.includes('Experience') ? 'Experience' : title.includes('Education') ? 'Education' : 'Certification'}
            </button>
        </div>
    </div>
);

const ItemRow = ({ item, onClick }: { item: ResumeItem, onClick: () => void }) => (
    <div className="group relative bg-white border border-[#015FA6]/20 rounded-xl p-3 transition-all hover:border-[#015FA6] hover:shadow-md cursor-pointer" onClick={onClick}>
        <div className="flex justify-between items-start">
            <div className="pr-8 text-left">
                {item.type === 'experience' && (
                    <>
                        <h4 className="text-[#015FA6] font-bold text-sm leading-tight">
                            {item.title} — <span className="font-semibold opacity-80">{item.company}</span>
                        </h4>
                        <p className="text-[#015FA6] text-xs font-semibold mt-1 opacity-70">{item.period}</p>
                        <p className="text-[#015FA6] text-xs mt-2 opacity-60 line-clamp-2">{item.description}</p>
                    </>
                )}
                {item.type === 'education' && (
                    <>
                        <h4 className="text-[#015FA6] font-bold text-sm leading-tight">
                            {item.degree} —
                        </h4>
                        <p className="text-[#015FA6] font-semibold text-xs opacity-80 mt-1">{item.school}</p>
                        <p className="text-[#015FA6] text-xs mt-1 opacity-70">{item.details}</p>
                        <p className="text-[#015FA6] text-xs mt-1 opacity-50">{item.year}</p>
                    </>
                )}
                {item.type === 'certification' && (
                    <>
                        <h4 className="text-[#015FA6] font-bold text-sm leading-tight">
                            {item.name}
                        </h4>
                        <p className="text-[#015FA6] font-semibold text-xs opacity-80 mt-1">{item.issuer}</p>
                        <p className="text-[#015FA6] text-xs mt-1 opacity-50">{item.year}</p>
                    </>
                )}
            </div>
            <button
                className="absolute top-3 right-3 text-[#015FA6] opacity-50 hover:opacity-100 hover:scale-110 transition-all p-1"
                aria-label="Edit"
            >
                <Edit2 size={16} />
            </button>
        </div>
    </div>
);

const ProfileReview: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<ResumeItem[]>([]);
    const [editingItem, setEditingItem] = useState<ResumeItem | null>(null);
    const [addingType, setAddingType] = useState<'experience' | 'education' | 'certification' | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Flow protection - requires coming from resume confirmation or login redirect
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromResumeConfirmation',
        allowIncompleteProfile: true,
        fallbackPath: '/candidate/dashboard'
    });

    // Redirect users who have already completed onboarding
    useOnboardingRedirect();

    const handleBack = () => {
        navigate('/welcome/resume-confirmation');
    };

    // Save profile data to backend
    const saveProfileData = async () => {
        // Convert UI data to backend format
        const experienceItems = data.filter(i => i.type === 'experience') as ExperienceItem[];
        const educationItems = data.filter(i => i.type === 'education') as EducationItem[];
        const certificationItems = data.filter(i => i.type === 'certification') as CertificationItem[];

        const experience = experienceItems.map(exp => ({
            title: exp.title,
            company: exp.company,
            start_date: exp.period.split('–')[0]?.trim() || '',
            end_date: exp.period.includes('Present') ? '' : exp.period.split('–')[1]?.trim() || '',
            is_current: exp.period.includes('Present'),
            description: exp.description,
            responsibilities: exp.description ? [exp.description] : []
        }));

        const education = educationItems.map(edu => ({
            degree: edu.degree,
            institution: edu.school,
            field_of_study: edu.details,
            end_date: edu.year
        }));

        const certifications = certificationItems.map(cert => ({
            name: cert.name,
            issuer: cert.issuer,
            issue_date: cert.year
        }));

        try {
            // Use /me endpoint which uses JWT token (no ID needed)
            await onboardingApi.updateCurrentPreferences({
                parsed_resume: {
                    experience,
                    education,
                    certifications
                }
            });
            console.log('[ProfileReview] Profile data saved successfully');
            return true;
        } catch (err: any) {
            console.error('[ProfileReview] Failed to save profile data:', err);
            setError('Failed to save profile changes: ' + err.message);
            return false;
        }
    };

    const handleNext = async () => {
        setSaving(true);
        try {
            const saved = await saveProfileData();
            if (saved) {
                // Set flow data for next page
                setFlowData('fromProfileReview', { reviewed: true });
                navigate('/welcome/career-goals');
            }
        } finally {
            setSaving(false);
        }
    };

    // Fetch candidate data on mount
    useEffect(() => {
        fetchCandidateData();
    }, []);

    const fetchCandidateData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch candidate profile using /me endpoint (JWT-based auth)
            const response = await candidateServiceApi.getCandidateProfile();

            if (response.success && response.candidate) {
                const candidate = response.candidate;

                // Load parsed resume data
                if (candidate.parsedResume) {
                    loadCandidateData(candidate.parsedResume);
                } else {
                    console.warn('No parsed resume data found');
                    setData([]);
                }
            } else {
                throw new Error('Failed to fetch candidate profile');
            }
        } catch (err: any) {
            console.error('Error fetching candidate data:', err);
            setError(err.message || 'Failed to load profile data');
        } finally {
            setLoading(false);
        }
    };

    const loadCandidateData = (parsedResume: any) => {
        const experiences: ExperienceItem[] = (parsedResume.experience || []).map((exp: any, idx: number) => ({
            id: `exp${idx + 1}`,
            type: 'experience' as const,
            title: exp.title || '',
            company: exp.company || '',
            period: `${exp.startDate || ''} – ${exp.isCurrent || exp.is_current ? 'Present' : exp.endDate || ''}`,
            description: exp.description || exp.responsibilities?.join('. ') || ''
        }));

        const educations: EducationItem[] = (parsedResume.education || []).map((edu: any, idx: number) => ({
            id: `edu${idx + 1}`,
            type: 'education' as const,
            degree: edu.degree || '',
            school: edu.institution || '',
            details: edu.fieldOfStudy || edu.field_of_study || '',
            year: edu.endYear || edu.endDate || edu.startDate || ''
        }));

        const certifications: CertificationItem[] = (parsedResume.certifications || []).map((cert: any, idx: number) => ({
            id: `cert${idx + 1}`,
            type: 'certification' as const,
            name: cert.name || '',
            issuer: cert.issuer || '',
            year: cert.year || cert.date || ''
        }));

        setData([...experiences, ...educations, ...certifications]);
    };

    const handleSave = (updatedItem: ResumeItem) => {
        if (updatedItem.id.startsWith('new-')) {
            // Adding new item
            setData(prev => [...prev, updatedItem]);
        } else {
            // Updating existing item
            setData(prev => prev.map(item => item.id === updatedItem.id ? updatedItem : item));
        }
        setAddingType(null);
    };

    const handleAdd = (type: 'experience' | 'education' | 'certification') => {
        setAddingType(type);
        setEditingItem(null);
    };

    // Don't render until flow access is validated
    if (isValidAccess === null) {
        return null;
    }

    // Show loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
                <BackgroundGrid />
                <div className="relative z-10 flex items-center justify-center h-screen">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 text-[#015FA6] animate-spin mx-auto mb-4" />
                        <p className="text-[#015FA6] text-lg font-medium">Loading your profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
                <BackgroundGrid />
                <div className="relative z-10 flex items-center justify-center h-screen">
                    <div className="text-center max-w-md mx-auto px-4">
                        <div className="text-red-500 mb-4">⚠️</div>
                        <h2 className="text-[#015FA6] text-2xl font-bold mb-2">Error Loading Profile</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <button
                            onClick={fetchCandidateData}
                            className="px-6 py-3 rounded-lg bg-[#015FA6] text-white font-bold hover:bg-[#014f8c] transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
            <BackgroundGrid />

            {/* Header */}
            <header className="relative z-10 pt-8 pb-4 text-center">
                <div className="mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        style={{
                            width: '100px',
                            height: '46px',
                            objectFit: 'contain',
                            margin: '0 auto'
                        }}
                    />
                </div>
                <Stepper currentStep="review" />
            </header>

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full py-8 px-4">
                {/* Header Section */}
                <div className="space-y-3 px-4 w-full text-center mb-8 animate-fade-in">
                    <h1 className="text-[#015FA6] font-bold text-[24px] sm:text-[32px] md:text-[40px] lg:text-[48px] leading-[100%] tracking-tight mb-4">
                        Let's make sure your information looks right.
                    </h1>
                    <p className="text-[#015FA6] font-extralight text-[16px] sm:text-[18px] md:text-[20px] lg:text-[24px] leading-[150%] max-w-6xl mx-auto">
                        We've pulled the key details from your resume — review and edit anything you'd like.
                    </p>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full pb-20 max-w-[1100px] mx-auto w-full">
                    <Card title="Experience" icon={Briefcase} onAdd={() => handleAdd('experience')}>
                        {data.filter(i => i.type === 'experience').map(item => (
                            <ItemRow key={item.id} item={item} onClick={() => setEditingItem(item)} />
                        ))}
                    </Card>

                    <Card title="Education" icon={GraduationCap} onAdd={() => handleAdd('education')}>
                        {data.filter(i => i.type === 'education').map(item => (
                            <ItemRow key={item.id} item={item} onClick={() => setEditingItem(item)} />
                        ))}
                    </Card>

                    <Card title="Licenses & Certifications" icon={Award} onAdd={() => handleAdd('certification')}>
                        {data.filter(i => i.type === 'certification').map(item => (
                            <ItemRow key={item.id} item={item} onClick={() => setEditingItem(item)} />
                        ))}
                    </Card>
                </div>

                {/* Footer Navigation */}
                <footer className="fixed bottom-0 left-0 right-0 z-20 py-6 px-4 bg-[#F8F9FB]/90 backdrop-blur-sm border-t border-gray-200">
                    <div className="max-w-4xl mx-auto flex justify-between items-center gap-4">
                        <button
                            onClick={() => navigate('/welcome/resume-confirmation')}
                            className="flex items-center gap-2 px-8 py-3 rounded-lg bg-[#9A9A9A] text-white font-medium hover:bg-gray-600 transition-colors shadow-lg shadow-gray-400/20"
                        >
                            Back
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={saving}
                            className="flex items-center gap-2 px-12 py-3 rounded-lg bg-[#015FA6] text-white font-bold hover:bg-[#014f8c] transition-colors shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Next'
                            )}
                        </button>
                    </div>
                </footer>
            </div>

            <EditModal
                isOpen={!!editingItem || !!addingType}
                onClose={() => {
                    setEditingItem(null);
                    setAddingType(null);
                }}
                item={editingItem}
                itemType={addingType || undefined}
                onSave={handleSave}
            />

            {/* Custom Styles */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #CBD5E1;
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94A3B8;
                }

                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }

                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.3s ease-out forwards;
                }

                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(40px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slide-up {
                    animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </div>
    );
};

export default ProfileReview;
